import { ECDH_KEY_SPEC, encodeBytes } from "open-signer-shared-cryptography";
import type { FrameLogger } from "../logger";
import { CrossmintFrameService } from "../service";
import {
	ENCRYPTION_KEYS_STORE_NAME,
	type FrameStorageAdapter,
} from "../storage";

export const IDENTITY_STORAGE_KEY = "identity-key";
export type IdentityStorageKey = typeof IDENTITY_STORAGE_KEY;
const HASH_ALGORITHM = "SHA-256" as const;

export class DeviceService extends CrossmintFrameService {
	name = "Device Service";
	log_prefix = "[DeviceService]";

	constructor(
		private readonly storage: FrameStorageAdapter,
		protected readonly logger: FrameLogger,
	) {
		super(logger);
	}

	public async getId(): Promise<string> {
		this.logger.debug(this.log_prefix, `Getting device ID`);
		const { publicKey } = await this.getIdentityKeys();
		const bytes = await crypto.subtle.exportKey("raw", publicKey);
		const hash = await crypto.subtle.digest(HASH_ALGORITHM, bytes);
		return encodeBytes(new Uint8Array(hash), "hex");
	}

	public async getSerializedIdentityPublicKey(): Promise<string> {
		const { publicKey } = await this.getIdentityKeys();
		const bytes = await crypto.subtle.exportKey("raw", publicKey);
		return encodeBytes(new Uint8Array(bytes), "base64");
	}

	public async getIdentityKeys(): Promise<CryptoKeyPair> {
		const stored = await this.storage.getItem(
			ENCRYPTION_KEYS_STORE_NAME,
			IDENTITY_STORAGE_KEY,
		);

		if (stored != null) {
			this.logger.debug(this.log_prefix, `Using existing identity keys`);
			return stored;
		}

		this.logger.debug(this.log_prefix, `Generating new identity keys`);
		const keys = await crypto.subtle.generateKey(
			ECDH_KEY_SPEC,
			false, // non-extractable
			["deriveBits", "deriveKey"], // to derive encryption keys
		);

		await this.storage.setItem(
			ENCRYPTION_KEYS_STORE_NAME,
			IDENTITY_STORAGE_KEY,
			keys,
		);

		return keys;
	}

	public async safeDebugState(): Promise<Record<string, unknown>> {
		const deviceId = await this.getId().catch(() => "<unaccessible>");
		const devicePublicKey = await this.getSerializedIdentityPublicKey()
			.then((serializedPublicKey) => ({
				bytes: serializedPublicKey,
				encoding: "base64",
				keyType: "p256",
			}))
			.catch(() => "<unaccessible>");

		return {
			devicePublicKey,
			deviceId,
		};
	}
}
